import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Field } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", email: "", role: "admin", password: "" };

export const AddAdminDialog = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(EMPTY); setErrs({}); } }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/team", f);
      toast.success(`${f.name} added — they'll set a new password at first login`);
      onOpenChange(false);
      onSaved();
    } catch (ex) {
      const { message, fields } = apiError(ex);
      setErrs(Object.keys(fields).length ? fields : { form: message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-admin-dialog">
        <form onSubmit={save} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add admin</DialogTitle>
            <DialogDescription>They'll be asked to choose their own password the first time they sign in.</DialogDescription>
          </DialogHeader>
          <Field label="Name" name="name" error={errs.name}><Input value={f.name} onChange={set("name")} data-testid="add-admin-name-input" /></Field>
          <Field label="Email" name="email" error={errs.email}><Input type="email" value={f.email} onChange={set("email")} data-testid="add-admin-email-input" /></Field>
          <Field label="Role" name="role" error={errs.role}>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger data-testid="add-admin-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin" data-testid="add-admin-role-admin">Admin</SelectItem>
                <SelectItem value="super_admin" data-testid="add-admin-role-super">Super admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Temporary password" name="password" error={errs.password} hint="At least 8 characters">
            <Input value={f.password} onChange={set("password")} data-testid="add-admin-password-input" />
          </Field>
          {errs.form && <p className="text-sm text-destructive" data-testid="add-admin-error">{errs.form}</p>}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="add-admin-save-button">{busy && <Loader2 className="animate-spin" />} Add admin</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const TempPasswordDialog = ({ admin, onClose, onSaved }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPw(""); setErr(""); }, [admin]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/team/${admin.id}/reset-password`, { password: pw });
      toast.success(data.message);
      onClose();
      onSaved();
    } catch (ex) {
      const { message, fields } = apiError(ex);
      setErr(fields.password || message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!admin} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="team-reset-dialog">
        <form onSubmit={save} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Reset password</DialogTitle>
            <DialogDescription>Set a temporary password for {admin?.name}. They must change it at next login.</DialogDescription>
          </DialogHeader>
          <Field label="Temporary password" name="password" error={err}><Input value={pw} onChange={(e) => setPw(e.target.value)} data-testid="team-reset-password-input" /></Field>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="team-reset-save-button">{busy && <Loader2 className="animate-spin" />} Set temporary password</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ConfirmDialog = ({ state, onClose, onConfirm }) => {
  const remove = state?.kind === "remove";
  return (
    <AlertDialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="team-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{remove ? "Remove" : "Deactivate"} {state?.a.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            {remove ? "Their login will be deleted. Their name stays on past ledger and activity records." : "They'll be signed out immediately and can't sign in until reactivated."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="team-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm(state)} data-testid="team-confirm-button">{remove ? "Remove" : "Deactivate"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
